// boot/ky.js
import ky from "ky";
import { backAdresse } from "./utils.js";
import { Notify } from "quasar";
import SessionStorageService from "src/services/sessionStorageService";
const baseURL = backAdresse;

/**
 * Avec prefixUrl, ky exige un chemin relatif SANS slash initial (sinon :
 * "`input` must not begin with a slash when using `prefixUrl`").
 */
function normalizeUrlForKy(url) {
  if (url == null || typeof url !== "string") return url;
  if (/^https?:\/\//i.test(url)) return url;
  return url.replace(/^\/+/, "");
}

/**
 * ✅ Étape 1.2 complétée : Refresh token automatique dans ky.js
 *
 * Fonctionnalités :
 * - Détection automatique des erreurs 401 (token expiré)
 * - Refresh automatique du token avec retry de la requête originale
 * - Gestion de la queue de requêtes pour éviter les race conditions
 * - Utilisation de SessionStorageService pour la gestion sécurisée des tokens
 * - Déconnexion automatique si le refresh token est expiré
 */

// Flag pour éviter les refresh multiples simultanés (race condition)
let isRefreshing = false;
let failedQueue = [];

// WeakMap pour stocker les options associées à chaque requête
const requestOptionsMap = new WeakMap();

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Instance ky unique avec configuration
const kyApi = ky.create({
  prefixUrl: baseURL,
  timeout: 30000, // 30 secondes
  retry: {
    limit: 2,
    methods: ["get", "head", "options", "trace"], // Pas PUT/DELETE pour éviter doublons
    statusCodes: [
      408, // Request Timeout
      429, // Too Many Requests (rate limiting Directus)
      500, // Internal Server Error
      502, // Bad Gateway (proxy devant Directus)
      503, // Service Unavailable (Directus en maintenance)
      504, // Gateway Timeout
      // EXCLUS: 413 (Directus gère les limites de taille différemment)
      // EXCLUS: 401/403 (erreurs d'auth Directus à ne pas retry)
    ],
  },
  hooks: {
    beforeRequest: [
      (request, options) => {
        // Stocker les options pour les récupérer dans beforeError
        requestOptionsMap.set(request, options);

        // Auth : SessionStorage en priorité, sinon api.defaults (compat stores axios)
        if (!options.skipAuth) {
          const token = SessionStorageService.getToken();
          const bearer = token ? `Bearer ${token}` : null;
          const fromDefaults =
            api?.defaults?.headers?.common?.Authorization ?? null;
          const authHeader = bearer || fromDefaults;
          if (authHeader) {
            request.headers.set("Authorization", authHeader);
          }
        }

        // Log des requêtes avec statut auth
        const authStatus = options.skipAuth ? "🌐 PUBLIC" : "🔐 AUTH";
        //   `🚀 KY Request: ${authStatus} ${request.method} ${request.url}`
        // );
        if (process.env.NODE_ENV === "development") {
        }
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        // Log des réponses en développement
        //   `✅ KY Response: ${request.method} ${request.url} - ${response.status}`
        // );
        if (process.env.NODE_ENV === "development") {
        }
        return response;
      },
    ],
    beforeError: [
      async (error) => {
        // ✅ Gestion des erreurs SSL/TLS
        if (
          error.name === "TypeError" &&
          (error.message?.includes("fetch") ||
            error.message?.includes("network") ||
            error.message?.includes("Failed to fetch"))
        ) {
          // Ajouter un flag pour identifier les erreurs SSL
          error.isNetworkError = true;
          error.isSslError = true;
          return error;
        }

        // ✅ Gestion des erreurs d'authentification avec refresh automatique
        if (error.response?.status === 401) {
          const originalRequest = error.request;
          const originalOptions = requestOptionsMap.get(originalRequest) || {};

          // Si la requête est déjà en cours de retry ou si c'est la requête de refresh elle-même, déconnecter
          if (
            originalOptions._retry ||
            originalRequest.url?.includes("/auth/refresh")
          ) {
            // Refresh a échoué ou requête déjà retry, déconnecter l'utilisateur
            SessionStorageService.clearAll();

            if (
              typeof window !== "undefined" &&
              !originalOptions.skipAuthNotification
            ) {
              Notify.create({
                message: "Session expirée, veuillez vous reconnecter",
                type: "warning",
                position: "top",
                timeout: 3000,
              });
            }

            if (typeof window !== "undefined" && window.location) {
              const currentPath = window.location.pathname;
              if (currentPath !== "/") {
                setTimeout(() => {
                  window.location.href = "/";
                }, 1000);
              }
            }

            processQueue(error, null);
            return error;
          }

          // Si un refresh est déjà en cours, mettre la requête en queue
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                // Réessayer la requête originale avec le nouveau token
                // Extraire l'URL relative depuis la requête complète
                const url = normalizeUrlForKy(
                  originalRequest.url.replace(baseURL, ""),
                );
                const method = originalRequest.method.toLowerCase();

                // Créer les options avec le nouveau token
                const retryOptions = {
                  ...originalOptions,
                  headers: {
                    ...originalOptions.headers,
                    Authorization: `Bearer ${token}`,
                  },
                };

                // Réessayer selon la méthode HTTP (le body est déjà dans originalOptions.json ou originalOptions.body)
                switch (method) {
                  case "get":
                    return kyApi.get(url, retryOptions);
                  case "post":
                    return kyApi.post(url, retryOptions);
                  case "put":
                    return kyApi.put(url, retryOptions);
                  case "patch":
                    return kyApi.patch(url, retryOptions);
                  case "delete":
                    return kyApi.delete(url, retryOptions);
                  default:
                    return kyApi(url, retryOptions);
                }
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          // Marquer qu'on est en train de refresh
          isRefreshing = true;
          originalOptions._retry = true;

          const {
            aUnRefreshToken,
            rafraichirAccessTokenAvecMutex,
          } = await import("src/services/authRefreshService");

          if (!aUnRefreshToken()) {
            isRefreshing = false;
            SessionStorageService.clearAll();

            if (
              typeof window !== "undefined" &&
              !originalOptions.skipAuthNotification
            ) {
              Notify.create({
                message: "Session expirée, veuillez vous reconnecter",
                type: "warning",
                position: "top",
                timeout: 3000,
              });
            }

            processQueue(error, null);
            return error;
          }

          try {
            const access_token = await rafraichirAccessTokenAvecMutex();

            processQueue(null, access_token);
            isRefreshing = false;

            // Réessayer la requête originale avec le nouveau token
            // Extraire l'URL relative depuis la requête complète
            const url = normalizeUrlForKy(
              originalRequest.url.replace(baseURL, ""),
            );
            const method = originalRequest.method.toLowerCase();

            // Créer les options avec le nouveau token
            // Le body est déjà dans originalOptions.json ou originalOptions.body
            const retryOptions = {
              ...originalOptions,
              headers: {
                ...originalOptions.headers,
                Authorization: `Bearer ${access_token}`,
              },
            };

            // Réessayer selon la méthode HTTP
            switch (method) {
              case "get":
                return kyApi.get(url, retryOptions);
              case "post":
                return kyApi.post(url, retryOptions);
              case "put":
                return kyApi.put(url, retryOptions);
              case "patch":
                return kyApi.patch(url, retryOptions);
              case "delete":
                return kyApi.delete(url, retryOptions);
              default:
                return kyApi(url, retryOptions);
            }
          } catch (refreshError) {
            // Refresh échoué, déconnecter l'utilisateur
            isRefreshing = false;
            SessionStorageService.clearAll();

            if (
              typeof window !== "undefined" &&
              !originalOptions.skipAuthNotification
            ) {
              Notify.create({
                message: "Session expirée, veuillez vous reconnecter",
                type: "warning",
                position: "top",
                timeout: 3000,
              });
            }

            if (typeof window !== "undefined" && window.location) {
              const currentPath = window.location.pathname;
              if (currentPath !== "/") {
                setTimeout(() => {
                  window.location.href = "/";
                }, 1000);
              }
            }

            processQueue(refreshError, null);
            return Promise.reject(refreshError);
          }
        }

        // Log des erreurs en développement
        if (process.env.NODE_ENV === "development") {
          // Erreur détectée
        }
        return error;
      },
    ],
  },
});

// URLs des assets (compatibilité avec axios)
const assetUrl = `${baseURL}assets/`;
const assetUrlServer = `${backAdresse}assets/`;

/**
 * Adaptateur api compatible axios pour migration progressive vers ky.
 * Retourne { data, status } comme axios pour que useApi/useResource restent compatibles.
 * @private
 */
function toAxiosResponse(response, data) {
  return {
    data,
    status: response.status,
    headers: Object.fromEntries(response.headers.entries()),
    config: {},
  };
}

/**
 * Directus exige un nom de fichier sur la partie multipart (sinon POST /files → 400).
 * Compression / Capacitor peuvent fournir un Blob ou un File sans name.
 */
function filenameForFormDataPart(blob) {
  if (blob instanceof File && blob.name && blob.name.trim() !== "") {
    return blob.name;
  }
  const mime = blob.type || "";
  const stamp = Date.now();
  if (mime.includes("webp")) return `upload_${stamp}.webp`;
  if (mime.includes("jpeg") || mime.includes("jpg")) return `upload_${stamp}.jpg`;
  if (mime.includes("png")) return `upload_${stamp}.png`;
  if (mime.includes("gif")) return `upload_${stamp}.gif`;
  return `upload_${stamp}.bin`;
}

/**
 * Directus : champs non-fichiers (folder, title, …) avant le binaire.
 * @see https://github.com/directus/directus/issues/19174
 */
function appendObjectAsFormData(fd, data) {
  const entries = Object.entries(data).filter(
    ([, v]) => v !== undefined && v !== null
  );
  const meta = [];
  const fileParts = [];

  for (const [k, v] of entries) {
    if (Array.isArray(v) && v.length > 0) {
      const allBlobs = v.every(
        (item) => item instanceof File || item instanceof Blob
      );
      if (allBlobs) {
        for (const item of v) {
          fileParts.push([k, item]);
        }
        continue;
      }
    }
    if (v instanceof File || v instanceof Blob) {
      fileParts.push([k, v]);
    } else {
      meta.push([k, v]);
    }
  }

  for (const [k, v] of meta) {
    fd.append(k, typeof v === "string" || typeof v === "number" ? v : String(v));
  }
  for (const [k, v] of fileParts) {
    fd.append(k, v, filenameForFormDataPart(v));
  }
}

async function runRequest(kyInstance, config) {
  const {
    method = "GET",
    url: rawUrl,
    params,
    headers = {},
    data,
    withCredentials = false,
    skipAuth = false,
    ...rest
  } = config;

  const url = normalizeUrlForKy(rawUrl);

  const isFormData = data instanceof FormData;
  const hasFile =
    data &&
    typeof data === "object" &&
    (data.file instanceof File ||
      data.file instanceof Blob ||
      (Array.isArray(data.file) &&
        data.file.length > 0 &&
        (data.file[0] instanceof File || data.file[0] instanceof Blob)));
  const isFilesEndpoint =
    typeof url === "string" &&
    (url === "files" || url.includes("/files"));
  const useFormData = isFormData || (hasFile && isFilesEndpoint);

  const m = method.toLowerCase();
  const canHaveBody = m !== "get" && m !== "head";

  let bodyOpt = {};
  if (canHaveBody && data !== undefined && data !== null) {
    if (useFormData) {
      const fd = isFormData ? data : new FormData();
      if (!isFormData && data) {
        appendObjectAsFormData(fd, data);
      }
      bodyOpt = { body: fd };
    } else {
      bodyOpt = { json: data };
    }
  }

  const cleanHeaders = Object.fromEntries(
    Object.entries(headers).filter(([, v]) => v != null)
  );
  // Nettoyer les params : retirer undefined/null, et [] pour limit (Directus attend un nombre)
  // Sérialiser filter/aggregate/deep en JSON (objet → "[object Object]" sinon)
  const DIRECTUS_JSON_PARAMS = ["filter", "aggregate", "deep"];
  const cleanParams =
    params && typeof params === "object"
      ? (() => {
          const sp = new URLSearchParams();
          for (const [k, v] of Object.entries(params)) {
            if (v == null || v === "") continue;
            if (k === "limit" && Array.isArray(v) && v.length === 0) continue;
            const strVal =
              DIRECTUS_JSON_PARAMS.includes(k) && (typeof v === "object" || Array.isArray(v))
                ? JSON.stringify(v)
                : String(v);
            sp.set(k, strVal);
          }
          return sp.size > 0 ? sp : undefined;
        })()
      : undefined;
  // Pour GET/HEAD : exclure body/json/params/searchParams (éviter d'écraser cleanParams)
  const safeRest =
    canHaveBody ? rest : Object.fromEntries(
      Object.entries(rest).filter(([k]) =>
        !["body", "json", "params", "searchParams"].includes(k)
      )
    );
  const kyOptions = {
    searchParams: cleanParams,
    headers: new Headers(cleanHeaders),
    credentials: withCredentials ? "include" : "omit",
    skipAuth: skipAuth || rest.skipAuth,
    ...bodyOpt,
    ...safeRest,
  };

  // Laisser le navigateur définir multipart + boundary (sinon 400/500 côté API)
  if (bodyOpt.body instanceof FormData) {
    kyOptions.headers.delete("content-type");
  }

  let response;
  if (m === "get") {
    response = await kyInstance.get(url, kyOptions);
  } else if (m === "post") {
    response = await kyInstance.post(url, kyOptions);
  } else if (m === "put") {
    response = await kyInstance.put(url, kyOptions);
  } else if (m === "patch") {
    response = await kyInstance.patch(url, kyOptions);
  } else if (m === "delete") {
    response = await kyInstance.delete(url, kyOptions);
  } else {
    response = await kyInstance(url, { method: m, ...kyOptions });
  }

  const ct = response.headers.get("content-type") || "";
  let parsed =
    ct.includes("application/json") || ct === ""
      ? await response.json().catch(() => ({}))
      : {};

  return toAxiosResponse(response, parsed);
}

/**
 * Instance api compatible axios (basée sur ky).
 * Utilisée par useApi, useResource, etc.
 * Supporte api(config), api.get(), api.post(), etc.
 */
async function api(config) {
  return runRequest(kyApi, config);
}
api.defaults = { headers: { common: {} } };
api.request = (cfg) => runRequest(kyApi, cfg);
api.get = (url, config = {}) => {
  const { params, ...rest } = config;
  return runRequest(kyApi, { method: "GET", url, params, ...rest });
};
api.post = (url, data, config = {}) => {
  const { params, ...rest } = config || {};
  return runRequest(kyApi, {
    method: "POST",
    url,
    data,
    params,
    ...rest,
  });
};
api.put = (url, data, config = {}) => {
  const { params, ...rest } = config || {};
  return runRequest(kyApi, {
    method: "PUT",
    url,
    data,
    params,
    ...rest,
  });
};
api.patch = (url, data, config = {}) => {
  const { params, ...rest } = config || {};
  return runRequest(kyApi, {
    method: "PATCH",
    url,
    data,
    params,
    ...rest,
  });
};
api.delete = (url, config = {}) => {
  const { params, ...rest } = config || {};
  return runRequest(kyApi, {
    method: "DELETE",
    url,
    params,
    ...rest,
  });
};

/**
 * Composable useApi (compatibilité avec boot/axios)
 */
export function useApi() {
  return {
    api,
    assetUrl,
    assetUrlServer,
  };
}

// Composable principal
export function useKy() {
  const n = normalizeUrlForKy;
  return {
    kyApi,
    assetUrl,
    assetUrlServer,
    // Méthodes helper pour simplicité d'usage (avec auth automatique)
    get: (url, options = {}) => kyApi.get(n(url), options).json(),
    post: (url, data = {}, options = {}) =>
      kyApi.post(n(url), { json: data, ...options }).json(),
    put: (url, data = {}, options = {}) =>
      kyApi.put(n(url), { json: data, ...options }).json(),
    patch: (url, data = {}, options = {}) =>
      kyApi.patch(n(url), { json: data, ...options }).json(),
    delete: (url, options = {}) => kyApi.delete(n(url), options).json(),

    // Méthodes pour appels publics Directus (sans token)
    getPublic: (url, options = {}) =>
      kyApi.get(n(url), { skipAuth: true, ...options }).json(),
    postPublic: (url, data = {}, options = {}) =>
      kyApi.post(n(url), { json: data, skipAuth: true, ...options }).json(),

    // Méthodes pour upload de fichiers
    postForm: (url, formData, options = {}) =>
      kyApi.post(n(url), { body: formData, ...options }).json(),
    putForm: (url, formData, options = {}) =>
      kyApi.put(n(url), { body: formData, ...options }).json(),

    // Méthodes raw (sans .json() automatique)
    getRaw: (url, options = {}) => kyApi.get(n(url), options),
    postRaw: (url, data = {}, options = {}) =>
      kyApi.post(n(url), { json: data, ...options }),
    putRaw: (url, data = {}, options = {}) =>
      kyApi.put(n(url), { json: data, ...options }),
    deleteRaw: (url, options = {}) => kyApi.delete(n(url), options),
  };
}

// Exports nommés pour compatibilité
export { kyApi, assetUrl, assetUrlServer, baseURL, api };

/** Boot Quasar : charge le module (kyApi + intercepteurs + api). */
export default function bootKy() {}
